import type { ReactNode } from 'react'
import { ConfigProvider, theme } from 'antd'

// 与原 llmbot 页面（LLM Bot / Batch Call / Channel 等）一致的暗色调色板。
// 来源：src/styles/table.scss、baseAntdUI.scss、pages/layout/index.module.scss、topNav 抽屉样式。
export const aiccPalette = {
  bg: '#090A0D',
  panel: '#121417',
  border: '#2A2D36',
  divider: '#383A40',
  text: '#E5E8EF',
  muted: '#A3AAB8',
  subtle: '#717787',
  placeholder: '#515766',
  accent: '#25D1F7',
  accentBg: '#25D1F714',
  blue: '#5793FA',
  hover: 'rgba(59, 63, 70, 0.52)',
}

const p = aiccPalette

// 独立 cssVar key：AICC 组件不带 .botlab 类，避开全局 `.botlab.ant-btn { width: 90px }` 等为浅色页写的覆盖。
export default function AiccTheme({ children }: { children: ReactNode }) {
  return <ConfigProvider
    theme={{
      algorithm: theme.darkAlgorithm,
      cssVar: { key: 'aicc' },
      token: {
        colorPrimary: p.accent,
        colorInfo: p.blue,
        colorLink: p.blue,
        colorLinkHover: p.accent,
        colorBgBase: p.bg,
        colorBgLayout: p.bg,
        colorBgContainer: p.bg,
        colorBgElevated: p.panel,
        colorBorder: p.border,
        colorBorderSecondary: p.border,
        colorSplit: p.border,
        colorText: p.text,
        colorTextSecondary: p.muted,
        colorTextTertiary: p.subtle,
        colorTextQuaternary: p.placeholder,
        colorTextPlaceholder: p.placeholder,
        colorTextHeading: p.text,
        colorFillAlter: p.panel,
        borderRadius: 10,
        borderRadiusSM: 5,
        fontSize: 13,
        controlHeight: 32,
      },
      components: {
        Button: {
          defaultBg: 'transparent',
          defaultColor: p.muted,
          defaultBorderColor: '#4E5767',
          defaultHoverBg: p.accentBg,
          defaultHoverColor: p.accent,
          defaultHoverBorderColor: p.accent,
          defaultActiveBg: p.accentBg,
          defaultActiveColor: p.accent,
          defaultActiveBorderColor: p.accent,
          primaryShadow: 'none',
          defaultShadow: 'none',
          dangerShadow: 'none',
          fontWeight: 400,
        },
        Table: {
          headerBg: p.panel,
          headerColor: p.subtle,
          headerSplitColor: 'transparent',
          headerBorderRadius: 10,
          rowHoverBg: p.hover,
          borderColor: p.border,
          colorBgContainer: p.bg,
          cellPaddingBlock: 12,
          fixedHeaderSortActiveBg: p.panel,
        },
        Pagination: {
          itemActiveBg: p.blue,
          itemBg: 'transparent',
          itemSize: 24,
        },
        Card: { colorBgContainer: p.bg, headerBg: p.bg, colorBorderSecondary: p.border },
        Drawer: { colorBgElevated: p.panel },
        Modal: { contentBg: p.panel, headerBg: p.panel, footerBg: p.panel, titleColor: p.text },
        Select: { optionSelectedBg: p.accentBg, optionActiveBg: 'rgba(255, 255, 255, 0.04)', selectorBg: p.bg },
        Input: { activeBorderColor: p.accent, hoverBorderColor: p.accent, activeShadow: 'none' },
        Tabs: { itemColor: p.muted, itemSelectedColor: p.accent, itemHoverColor: p.text, inkBarColor: p.accent },
        Steps: { colorPrimary: p.blue },
        Descriptions: { labelBg: p.panel, colorSplit: p.border },
        Alert: { colorInfoBg: 'rgba(87, 147, 250, 0.08)', colorInfoBorder: 'rgba(87, 147, 250, 0.35)' },
      },
    }}
    wave={{ disabled: true }}
  >
    {children}
  </ConfigProvider>
}
